import React, { createContext, useEffect, useState, useCallback } from 'react'
import cookies from 'js-cookie'
import { decodeGlobalId } from '~/utils/id'
import { useViewer } from '~/components/ViewerContext'

// The following function content is copy pasted from
// https://developers.intercom.com/installing-intercom/docs/basic-javascript
// We keep it like this so we can easily copy paste an update if needed.
const initializeIntercom = appId => {
  const intercom = window.Intercom
  if (typeof intercom === 'function') {
    intercom('reattach_activator')
    intercom('update', window.intercomSettings)
  } else {
    const instance = function () {
      instance.c(arguments)
    }
    instance.q = []
    instance.c = function (args) {
      instance.q.push(args)
    }
    window.Intercom = instance
    const load = function () {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`
      const element = document.getElementsByTagName('script')[0]
      element.parentNode.insertBefore(script, element)
    }
    if (document.readyState === 'complete') {
      load()
    } else if (window.attachEvent) {
      window.attachEvent('onload', load)
    } else {
      window.addEventListener('load', load, false)
    }
  }
}

const IntercomContext = createContext()
export const IntercomConsumer = IntercomContext.Consumer

export function useIntercom() {
  const context = React.useContext(IntercomContext)

  if (!context) {
    throw new Error('useIntercom() must be used within a IntercomProvider')
  }

  return context
}

export function IntercomProvider({ children, appId }) {
  const engaged = cookies.get('Intercom-engaged') === '1'
  const show = cookies.get('Intercom-show') === '1'
  const [enabled, setEnabled] = useState(false)
  const { viewer } = useViewer()
  const { id, firstname: name, email, intercomVerificationId: user_hash } = viewer || {}
  const options = viewer
    ? {
        user_id: decodeGlobalId(id),
        name,
        email,
        user_hash,
      }
    : {}

  const initialize = useCallback(() => {
    if (!window.Intercom) {
      setEnabled(true)
      cookies.set('Intercom-engaged', '1')
      initializeIntercom(appId)
    }
  }, [appId, setEnabled])

  const boot = useCallback(() => {
    if (!window.Intercom.booted) {
      window.Intercom('boot', {
        app_id: appId,
        ...options,
      })
    } else {
      window.Intercom('update', {
        ...options,
      })
    }
  }, [appId, options])

  const setupEventHandlers = useCallback(() => {
    window.Intercom('onHide', () => cookies.set('Intercom-show', '0'))
    window.Intercom('onShow', () => cookies.set('Intercom-show', '1'))
  }, [])

  const openChat = useCallback(() => {
    window.Intercom('show')
  }, [])

  useEffect(() => {
    if (!enabled && engaged) {
      initialize()
      boot()
      setupEventHandlers()
      show && openChat()
    }
  }, [enabled, engaged, initialize, boot, setupEventHandlers, show, openChat])

  const start = () => {
    initialize()
    boot()
    setupEventHandlers()
    openChat()
  }

  return <IntercomContext.Provider value={{ start }}>{children}</IntercomContext.Provider>
}
